import { AxiosResponse } from 'axios';
import { all, call, fork, put, takeLatest } from 'redux-saga/effects';

import {
  normalizeDataSisGraphFieldsForFetch,
  resolveDataSisGraphFields,
  resolveDataSisGraphFieldsForSearch,
} from '../domain/dataSisGraphFields';
import {
  GraphSis,
  GraphSisApiResponse,
  GraphSisFieldItem,
  GraphSisParams,
  toGraphSisSetting,
} from '../domain/sis/types';

import {
  ChangeDataSisGraphDateTypeAction,
  ChangeDataSisGraphFieldAction,
  DataSisGraphActionCreators,
  DataSisGraphActionTypes,
  FetchDataSisGraphAction,
  SearchDataSisGraphAction,
  SetDataSisGraphFieldsAction,
  dataSisGraphSettingSelector,
} from '../redux/server/dataSisGraph';
import { SisSettingsActionCreators } from '../redux/ui/sisSettings';
import { Api, buildConfig } from '../utils/api';
import { customSelect } from '../utils/customSelect';
import { handleErrorSaga } from './errorSaga';

type GraphFieldFetchResult =
  | { field: string; response: GraphSisApiResponse }
  | { field?: string; error: unknown };

const isGraphFieldFetchSuccess = (
  result: GraphFieldFetchResult
): result is { field: string; response: GraphSisApiResponse } =>
  'response' in result;

const isGraphFieldFetchFailure = (
  result: GraphFieldFetchResult
): result is { field?: string; error: unknown } => 'error' in result;

const toGraphApiParams = (
  params: GraphSisParams,
  field?: string
): GraphSisParams => {
  const { field: _field, fields: _fields, ...commonParams } = params;
  return field ? { ...commonParams, field } : commonParams;
};

function* fetchGraphByField(api: Api, params: GraphSisParams, field?: string) {
  const response: AxiosResponse<GraphSisApiResponse> = yield call(
    api.get,
    '/data/sis/graph',
    buildConfig(toGraphApiParams(params, field))
  );
  return response.data;
}

function* fetchGraphByFieldSafely(
  api: Api,
  params: GraphSisParams,
  field?: string
): Generator<unknown, GraphFieldFetchResult, GraphSisApiResponse> {
  try {
    const response = yield call(fetchGraphByField, api, params, field);
    const resolvedField = field ?? response.setting.field;
    return resolvedField
      ? { field: resolvedField, response }
      : {
          error: new Error(
            'SIS graph API response does not contain setting.field'
          ),
        };
  } catch (error: unknown) {
    return { field, error };
  }
}

const buildGraphSis = (
  results: { field: string; response: GraphSisApiResponse }[]
): GraphSis => {
  const firstResponse = results.at(0)?.response;
  if (!firstResponse) {
    throw new Error('SIS graph API response is empty');
  }
  const graphList: GraphSisFieldItem[] = results.map(({ field, response }) => ({
    field,
    data: response.data,
  }));
  return {
    setting: toGraphSisSetting(firstResponse.setting),
    graphList,
  };
};

/**
 * dataSisGraphのデータを取得する。
 * fields 指定時は field ごとに並列取得し、未指定の初回は API 既定項目を維持する。
 */
export function* fetchDataSisGraphSaga(
  api: Api,
  action: FetchDataSisGraphAction
) {
  const { params } = action.payload;

  // 前回の閉じアニメーションが中断されフラグがfalseのまま残っていても開けるよう、取得開始時に有効化する
  yield put(
    SisSettingsActionCreators.selectSisGraphAnimationEnabledAction(true)
  );

  yield put(DataSisGraphActionCreators.fetchDataSisGraphRequestAction());
  const fields = resolveDataSisGraphFields(params);
  const targetFields: (string | undefined)[] = fields ?? [undefined];
  const results: GraphFieldFetchResult[] = yield all(
    targetFields.map((field) =>
      call(fetchGraphByFieldSafely, api, params, field)
    )
  );
  const successfulResults = results.filter(isGraphFieldFetchSuccess);
  const failedResults = results.filter(isGraphFieldFetchFailure);

  if (successfulResults.length === 0) {
    yield put(DataSisGraphActionCreators.renewDataSisGraphAction());
    const firstError = failedResults.at(0)?.error;
    if (firstError !== undefined) {
      yield fork(handleErrorSaga, firstError);
    }
    return;
  }

  try {
    yield put(
      DataSisGraphActionCreators.fetchDataSisGraphSuccessAction(
        buildGraphSis(successfulResults)
      )
    );
  } catch (error: unknown) {
    yield put(DataSisGraphActionCreators.renewDataSisGraphAction());
    yield fork(handleErrorSaga, error);
    return;
  }

  if (failedResults.length > 0) {
    console.warn(
      `[dataSisGraphSaga] 一部グラフ項目の取得に失敗しました: ${failedResults
        .map((result) => result.field ?? 'default')
        .join(', ')}`
    );
  }
}

function* refetchDataSisGraphWithFields(fields: string[]) {
  const searchCondition = yield* customSelect(dataSisGraphSettingSelector);
  if (!searchCondition) {
    console.warn(
      '[dataSisGraphSaga] グラフ検索条件が未設定のため再取得をスキップしました'
    );
    return;
  }
  yield put(
    DataSisGraphActionCreators.fetchDataSisGraphAction({
      ...searchCondition,
      fields: normalizeDataSisGraphFieldsForFetch(fields),
    })
  );
}

/**
 * 指定したグラフ項目の推移グラフを取得する
 */
function* changeDataSisGraphFieldSaga(action: ChangeDataSisGraphFieldAction) {
  yield* refetchDataSisGraphWithFields([action.payload.field]);
}

function* setDataSisGraphFieldsSaga(action: SetDataSisGraphFieldsAction) {
  yield* refetchDataSisGraphWithFields(action.payload.fields);
}

/**
 * 指定した期間の推移グラフを取得する
 */
function* changeDataSisGraphDateTypeSaga(
  action: ChangeDataSisGraphDateTypeAction
) {
  const { dateType } = action.payload;

  const searchCondition = yield* customSelect(dataSisGraphSettingSelector);
  if (!searchCondition) {
    console.warn(
      '[dataSisGraphSaga] グラフ検索条件が未設定のため期間変更をスキップしました'
    );
    return;
  }
  yield put(
    DataSisGraphActionCreators.fetchDataSisGraphAction({
      ...searchCondition,
      dateType,
    })
  );
}

function* searchDataSisGraphSaga(action: SearchDataSisGraphAction) {
  yield put(
    DataSisGraphActionCreators.changeDataSisGraphKiListAction(
      action.payload.kiList
    )
  );
  const existingParams = yield* customSelect(dataSisGraphSettingSelector);
  const fields = resolveDataSisGraphFieldsForSearch(existingParams);
  yield put(
    DataSisGraphActionCreators.fetchDataSisGraphAction({
      ...existingParams,
      kiList: action.payload.kiList,
      sisDataType: action.payload.selectedDataType,
      ...(fields ? { fields } : {}),
    })
  );
}

function* closeDataSisGraphSaga() {
  yield put(DataSisGraphActionCreators.renewDataSisGraphAction());
}

function* handleFetchDataSisGraphSaga(api: Api) {
  yield takeLatest(
    DataSisGraphActionTypes.FETCH_DATA_SIS_GRAPH,
    fetchDataSisGraphSaga,
    api
  );
}

function* handleSearchDataSisGraphSaga() {
  yield takeLatest(
    DataSisGraphActionTypes.SEARCH_DATA_SIS_GRAPH,
    searchDataSisGraphSaga
  );
}

function* handleChangeDataSisGraphSaga() {
  yield takeLatest(
    DataSisGraphActionTypes.CHANGE_DATA_SIS_GRAPH_FIELD,
    changeDataSisGraphFieldSaga
  );
  yield takeLatest(
    DataSisGraphActionTypes.CHANGE_DATA_SIS_GRAPH_DATE_TYPE,
    changeDataSisGraphDateTypeSaga
  );
  yield takeLatest(
    DataSisGraphActionTypes.SET_DATA_SIS_GRAPH_FIELDS,
    setDataSisGraphFieldsSaga
  );
}

function* handleCloseDataSisGraphSaga() {
  yield takeLatest(
    DataSisGraphActionTypes.CLOSE_DATA_SIS_GRAPH,
    closeDataSisGraphSaga
  );
}

export function* dataSisGraphSaga(context: { api: Api }) {
  yield fork(handleFetchDataSisGraphSaga, context.api);
  yield fork(handleSearchDataSisGraphSaga);
  yield fork(handleChangeDataSisGraphSaga);
  yield fork(handleCloseDataSisGraphSaga);
}
